/**
 * Offline photo + completion queue (IndexedDB).
 *
 * When an operative is on a site with no signal:
 *  - Photos they add to a job's Files section are stored as blobs in IndexedDB
 *    and uploaded automatically when the connection returns.
 *  - If they tap "Complete job" while offline, the completion payload is stored
 *    and fired automatically AFTER the queued photos finish uploading (the
 *    backend requires Before/After photos to exist before completing).
 */
import API from "./api";
import { toast } from "sonner";

const DB_NAME = "fixli_offline_media";
const DB_VERSION = 1;
const PHOTOS = "photos";
const COMPLETIONS = "completions";

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PHOTOS)) db.createObjectStore(PHOTOS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(COMPLETIONS)) db.createObjectStore(COMPLETIONS, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function tx(db, store, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const out = fn(s);
    t.oncomplete = () => resolve(out?.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
  });
}

function getAll(db, store) {
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, "readonly").objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

// ── listeners ────────────────────────────────────────────────────
const listeners = new Set();
let _counts = { photos: 0, completions: 0 };

async function refreshCounts() {
  try {
    const db = await openDb();
    const [photos, completions] = await Promise.all([getAll(db, PHOTOS), getAll(db, COMPLETIONS)]);
    _counts = { photos: photos.length, completions: completions.length };
  } catch {
    _counts = { photos: 0, completions: 0 };
  }
  listeners.forEach((fn) => { try { fn(_counts); } catch { /* ignore */ } });
  return _counts;
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(_counts);
  refreshCounts();
  return () => listeners.delete(fn);
}

export function counts() {
  return _counts;
}

// ── enqueue ──────────────────────────────────────────────────────

/** Queue files (File/Blob objects) to upload into a hub folder later. */
export async function queuePhotos(files, { folderId, entityType = "", entityId = "" }) {
  const db = await openDb();
  let queued = 0;
  for (const f of files) {
    const rec = {
      id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      blob: f,
      name: f.name || "photo.jpg",
      type: f.type || "image/jpeg",
      folder_id: folderId,
      entity_type: entityType,
      entity_id: entityId,
      queued_at: new Date().toISOString(),
    };
    await tx(db, PHOTOS, "readwrite", (s) => s.put(rec));
    queued += 1;
  }
  refreshCounts();
  return queued;
}

/** Queue a job/emergency completion to fire once photos have synced. */
export async function queueCompletion(entityType, entityId, payload) {
  const db = await openDb();
  await tx(db, COMPLETIONS, "readwrite", (s) => s.put({
    key: `${entityType}:${entityId}`,
    entity_type: entityType,
    entity_id: entityId,
    payload: payload || {},
    queued_at: new Date().toISOString(),
  }));
  refreshCounts();
}

export async function hasPendingForEntity(entityType, entityId) {
  const db = await openDb();
  const photos = await getAll(db, PHOTOS);
  return photos.some((p) => p.entity_type === entityType && p.entity_id === entityId);
}

// ── flush ────────────────────────────────────────────────────────
let flushing = false;

export async function flush() {
  if (flushing || typeof navigator === "undefined" || !navigator.onLine) return { uploaded: 0, completed: 0, failed: 0 };
  flushing = true;
  let uploaded = 0, completed = 0, failed = 0;
  try {
    const db = await openDb();

    // 1. Upload photos first (FIFO)
    const photos = (await getAll(db, PHOTOS)).sort((a, b) => (a.queued_at || "").localeCompare(b.queued_at || ""));
    for (const p of photos) {
      const fd = new FormData();
      fd.append("folder_id", p.folder_id);
      fd.append("file", new File([p.blob], p.name, { type: p.type }));
      try {
        await API.post("/files/upload", fd, { timeout: 60000 });
        await tx(db, PHOTOS, "readwrite", (s) => s.delete(p.id));
        uploaded += 1;
      } catch (err) {
        const status = err.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Permanent rejection — drop so the queue doesn't jam
          await tx(db, PHOTOS, "readwrite", (s) => s.delete(p.id));
          failed += 1;
          toast.error(`"${p.name}" could not be uploaded: ${err.response?.data?.detail || "rejected"}`);
        } else {
          // Network/transient — stop this round, keep everything queued
          refreshCounts();
          flushing = false;
          return { uploaded, completed, failed };
        }
      }
    }

    // 2. Fire pending completions (photos are now on the server)
    const comps = await getAll(db, COMPLETIONS);
    for (const c of comps) {
      try {
        await API.post(`/in-house-portal/${c.entity_type}/${c.entity_id}/complete`, c.payload, { timeout: 30000 });
        await tx(db, COMPLETIONS, "readwrite", (s) => s.delete(c.key));
        completed += 1;
      } catch (err) {
        const status = err.response?.status;
        if (status && status >= 400 && status < 500) {
          await tx(db, COMPLETIONS, "readwrite", (s) => s.delete(c.key));
          failed += 1;
          toast.error(`Queued completion failed: ${err.response?.data?.detail || "rejected"}`);
        }
        // transient → leave queued for the next flush
      }
    }
  } catch {
    /* IDB unavailable — nothing we can do */
  } finally {
    flushing = false;
    refreshCounts();
  }
  if (uploaded || completed) {
    toast.success(
      [uploaded ? `${uploaded} photo${uploaded > 1 ? "s" : ""} uploaded` : "",
       completed ? `${completed} job${completed > 1 ? "s" : ""} completed` : ""]
        .filter(Boolean).join(" · ") + " (offline sync)"
    );
  }
  return { uploaded, completed, failed };
}

// Auto-flush on reconnect + once on load
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { setTimeout(() => flush(), 500); });
  if (typeof navigator !== "undefined" && navigator.onLine) setTimeout(() => flush(), 2500);
}

const offlinePhotoQueue = { queuePhotos, queueCompletion, hasPendingForEntity, flush, subscribe, counts };
export default offlinePhotoQueue;
